/**
 * Which CRM the Prospects page is for.
 *
 * The integrations service decides which CRMs are being scored and which are paused, so this asks it
 * rather than keeping a list here that could disagree with it. What the app adds is the advisor's own
 * AppConfig flags: a CRM counts only if that CRM is switched on for them.
 *
 * - `active` is the CRM whose book the page shows: the first one the service is scoring that this
 *   advisor has. There is one at a time; no picker.
 * - `paused` are CRMs this advisor has that prospecting is paused for, so the page can say so
 *   instead of looking empty.
 *
 * Asked only when prospecting is on for the advisor at all, unless the caller says otherwise — the
 * integration cards need to know about a pause whether or not the advisor has the Prospects page.
 * The answer is remembered for a few minutes: it changes with a release, not while someone is working.
 *
 * Until the advisor's flags have arrived nothing can be said about which CRMs are theirs, so the hook
 * reports loading. Otherwise the page would say "not available" for a moment to an advisor who has it.
 */
import { useQuery } from '@tanstack/react-query';
import { prospectingApi, queryKeys } from '@client/api/integrations';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

const STALE_MS = 5 * 60_000;

export function useProspectingProviders(options: { enabled?: boolean } = {}) {
  const { flags, isLoaded: flagsLoaded } = useFeatureFlags();
  const enabled = options.enabled ?? !!flags?.crmProspecting;

  const query = useQuery({
    queryKey: queryKeys.prospecting.providers(),
    queryFn: () => prospectingApi.providers.list(),
    enabled,
    staleTime: STALE_MS,
  });

  const mine = (query.data?.providers ?? []).filter(entry => flags?.[entry.provider]);

  return {
    isLoading: !flagsLoaded || (enabled && query.isLoading),
    isError: query.isError,
    refetch: query.refetch,
    active: mine.find(entry => entry.status === 'active') ?? null,
    paused: mine.filter(entry => entry.status === 'paused'),
  };
}
